import axios from 'axios';
import { store } from '../store';
import { incrementRequests, decrementRequests, startApiCall, stopApiCall } from '../store/loadingSlice';

// Force CRA proxy on ajgh.local to avoid CORS; otherwise allow explicit env
const isAjghLocal =
  typeof window !== 'undefined' && window.location && window.location.hostname === 'ajgh.local';
const API_BASE_URL = isAjghLocal ? '/api' : (process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api');

const api = axios.create({
  baseURL: API_BASE_URL || undefined,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor for adding auth token and tracking loading
api.interceptors.request.use(
  (config) => {
    try {
      const key = `${(config.method || 'GET').toUpperCase()} ${config.url}`;
      store.dispatch(incrementRequests());
      store.dispatch(startApiCall(key));
      (config as any)._loadingKey = key;
    } catch {}
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Helper function for delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Response interceptor for handling errors, tracking loading, and token refresh
api.interceptors.response.use(
  (response) => {
    try {
      const key = (response.config as any)._loadingKey as string | undefined;
      if (key) {
        store.dispatch(stopApiCall(key));
      }
      store.dispatch(decrementRequests());
    } catch {}
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 429 rate limit errors with exponential backoff retry
    if (error.response?.status === 429) {
      const retryCount = originalRequest._retryCount || 0;
      const maxRetries = 3;

      if (retryCount < maxRetries) {
        originalRequest._retryCount = retryCount + 1;
        // Exponential backoff: 500ms, 1000ms, 2000ms
        const backoffDelay = Math.pow(2, retryCount) * 500;
        console.warn(`Rate limited (429). Retrying in ${backoffDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
        await delay(backoffDelay);
        return api(originalRequest);
      }
      // Max retries exceeded, fall through to error handling
      console.error('Max retries exceeded for rate limiting');
    }

    try {
      const key = (originalRequest as any)._loadingKey as string | undefined;
      if (key) {
        store.dispatch(stopApiCall(key));
      }
      store.dispatch(decrementRequests());
    } catch {}

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // No refresh token, redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('token'); // Legacy
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Try to refresh the token
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refreshToken },
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
          }
        );

        const { accessToken, refreshToken: newRefreshToken, user } = response.data;

        // Update tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', accessToken); // Legacy

        // Update the failed requests
        processQueue(null, accessToken);

        // Retry the original request
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear everything and redirect to login
        processQueue(refreshError, null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('token'); // Legacy
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// API Endpoints
export const companiesAPI = {
  getAll: (params?: any) => api.get('/companies', { params }),
  getById: (id: string) => api.get(`/companies/${id}`),
  getDirectors: (id: string, config?: any) => api.get(`/companies/${id}/directors`, config),
  addDirector: (id: string, payload: { personId?: string; personName?: string; roleTitle?: string; isNominee?: boolean; appointmentDate?: string; resignationDate?: string; notes?: string }) =>
    api.post(`/companies/${id}/directors`, payload),
  updateDirector: (id: string, directorId: string, payload: Partial<{ roleTitle: string; isNominee: boolean; appointmentDate: string; resignationDate: string; notes: string }>) =>
    api.patch(`/companies/${id}/directors/${directorId}`, payload),
  removeDirector: (id: string, directorId: string) => api.delete(`/companies/${id}/directors/${directorId}`),
  // Ownerships (shareholders)
  getOwnerships: (id: string, config?: any) => api.get(`/companies/${id}/ownerships`, config),
  addOwnership: (
    id: string,
    payload: {
      ownerType: 'PERSON' | 'COMPANY';
      ownerPersonId?: string;
      ownerPersonName?: string;
      ownerCompanyId?: string;
      ownerCompanyName?: string;
      ownershipPercentage?: number;
      numberOfSharesHeld?: number;
      sharesHeldRaw?: string;
      ownershipType?: string;
      isNomineeArrangement?: boolean;
      nomineeDetails?: string;
      notes?: string;
      effectiveDate?: string;
      endDate?: string;
    }
  ) => api.post(`/companies/${id}/ownerships`, payload),
  updateOwnership: (
    id: string,
    ownershipId: string,
    payload: Partial<{
      ownerType?: 'PERSON' | 'COMPANY';
      ownerPersonId?: string;
      ownerCompanyId?: string;
      ownershipPercentage: number;
      numberOfSharesHeld: number;
      sharesHeldRaw: string;
      ownershipType: string;
      isNomineeArrangement: boolean;
      nomineeDetails: string;
      notes: string;
      effectiveDate: string;
      endDate: string
    }>,
  ) => api.patch(`/companies/${id}/ownerships/${ownershipId}`, payload),
  removeOwnership: (id: string, ownershipId: string) => api.delete(`/companies/${id}/ownerships/${ownershipId}`),
  create: (payload: any) => api.post('/companies', payload),
  update: (id: string, payload: any) => api.patch(`/companies/${id}`, payload),
  remove: (id: string) => api.delete(`/companies/${id}`),
  getStats: () => api.get('/companies/stats'),
  getJurisdictions: () => api.get('/companies/jurisdictions'),
  getOwnershipGraph: (companyId?: string, jurisdiction?: string) =>
    api.get('/companies/ownership-graph', { params: { companyId, jurisdiction } }),
  getCompanyOwnershipGraph: (id: string) =>
    api.get(`/companies/${id}/ownership-graph`),
  getInsights: () => api.get('/companies/insights'),
  getWindUp: () => api.get('/companies/windup'),
  getTopOwners: (params?: { search?: string; type?: 'all' | 'person' | 'company'; role?: 'ALL' | 'DIRECTOR' | 'SHAREHOLDER'; page?: number; limit?: number }) =>
    api.get('/companies/top-owners', { params }),
  getFinancialStatementStatuses: (params: {
    periodType: 'MONTH' | 'FISCAL_YEAR';
    reportingMonth?: string;
    fiscalYear?: number;
    missingOnly?: boolean;
  }) => api.get('/companies/financial-statements/statuses', { params }),
  getFinancialStatementStatus: (
    id: string,
    params: {
      periodType: 'MONTH' | 'FISCAL_YEAR';
      reportingMonth?: string;
      fiscalYear?: number;
    },
  ) => api.get(`/companies/${id}/financial-statements/status`, { params }),
  // Enrichment endpoints
  startEnrichment: (id: string) => api.post(`/companies/${id}/enrichment/search`),
  getEnrichmentJob: (jobId: string) => api.get(`/enrichment/${jobId}`),
  applyEnrichment: (jobId: string, selectedFields: Array<{ field: string; newValue: any }>) =>
    api.post(`/enrichment/${jobId}/apply`, { selectedFields }),
  // Validation
  validateDirectors: (id: string) => api.post(`/companies/${id}/directors/validate`),
  // PDF Report
  downloadReport: (id: string) => {
    return api.get(`/companies/${id}/report/pdf`, {
      responseType: 'blob',
    });
  },
};

export const personsAPI = {
  getAll: (params?: any) => api.get('/persons', { params }),
  getById: (id: string) => api.get(`/persons/${id}`),
  getDirectorships: (id: string) => api.get(`/persons/${id}/directorships`),
  create: (payload: any) => api.post('/persons', payload),
  update: (id: string, payload: any) => api.patch(`/persons/${id}`, payload),
  remove: (id: string) => api.delete(`/persons/${id}`),
  merge: (payload: {
    targetPersonId: string;
    sourcePersonIds: string[];
    fieldStrategy?: Record<string, string>;
    createAliases?: boolean;
    dryRun?: boolean;
  }) => api.post('/persons/merge', payload),
  mergePreview: (payload: {
    targetPersonId: string;
    sourcePersonIds: string[];
    fieldStrategy?: Record<string, string>;
    createAliases?: boolean;
  }) => api.post('/persons/merge', { ...payload, dryRun: true }),
  // Profile photo endpoints
  uploadProfilePhoto: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    return api.post(`/persons/${id}/profile-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getProfilePhotoUrl: (id: string) => `${API_BASE_URL || ''}/persons/${id}/profile-photo`,
  deleteProfilePhoto: (id: string) => api.delete(`/persons/${id}/profile-photo`),
  // Graph endpoints
  getDirectorshipGraph: (id: string) => api.get(`/persons/${id}/directorship-graph`),
  getShareholdingGraph: (id: string) => api.get(`/persons/${id}/shareholding-graph`),
  getNetworkGraph: (id: string, include?: string) => api.get(`/persons/${id}/network-graph`, { params: { include } }),
};

export const searchAPI = {
  search: (params: { q: string; limit?: number }) =>
    api.get<{ companies: Array<{ id: string; name: string; jurisdiction: string; registrationNumber?: string }>; people: Array<{ id: string; name: string; email?: string; types?: string[] }> }>('/search', { params }),
};

export const ownershipAPI = {
  getAll: (params?: any) => api.get('/ownerships', { params }),
};

export const reportsAPI = {
  getQualityReport: () => api.get('/reports/quality'),
  
  // Ownership reports
  downloadOwnershipHierarchy: (params?: any) => 
    api.get('/reports/ownership/hierarchy', { params, responseType: 'blob' }),
  downloadBeneficialOwnership: (params?: any) => 
    api.get('/reports/ownership/beneficial', { params, responseType: 'blob' }),
  downloadShareholderRegister: (params?: any) => 
    api.get('/reports/ownership/shareholder-register', { params, responseType: 'blob' }),
  downloadCrossHoldings: (params?: any) => 
    api.get('/reports/ownership/cross-holdings', { params, responseType: 'blob' }),
  
  // Compliance reports
  downloadDirectorRegister: (params?: any) => 
    api.get('/reports/compliance/directors', { params, responseType: 'blob' }),
  downloadOfficerList: (params?: any) => 
    api.get('/reports/compliance/officers', { params, responseType: 'blob' }),
  downloadComplianceStatus: (params?: any) => 
    api.get('/reports/compliance/status', { params, responseType: 'blob' }),
  downloadChangesReport: (params?: any) => 
    api.get('/reports/compliance/changes', { params, responseType: 'blob' }),
  
  // Financial reports
  downloadCapitalStructure: (params?: any) => 
    api.get('/reports/financial/capital-structure', { params, responseType: 'blob' }),
  downloadShareCapitalAnalysis: (params?: any) => 
    api.get('/reports/financial/share-capital-analysis', { params, responseType: 'blob' }),
  downloadValuationSummary: (params?: any) => 
    api.get('/reports/financial/valuation', { params, responseType: 'blob' }),
};

export const documentsAPI = {
  // Upload document
  upload: (formData: FormData) =>
    api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Get documents by entity
  getByCompany: (companyId: string) => api.get(`/documents/company/${companyId}`),
  getByPerson: (personId: string) => api.get(`/documents/person/${personId}`),

  // Get single document
  getById: (id: string) => api.get(`/documents/${id}`),

  // Download document
  download: (id: string) =>
    api.get(`/documents/${id}/download`, {
      responseType: 'blob',
    }),

  // Update document metadata
  update: (id: string, payload: any) => api.patch(`/documents/${id}`, payload),

  // Replace document file as a new version
  replace: (id: string, formData: FormData) =>
    api.post(`/documents/${id}/replace`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Delete document
  delete: (id: string) => api.delete(`/documents/${id}`),
};

export const notificationsAPI = {
  list: (params?: { unreadOnly?: boolean; limit?: number }) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id: string) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (payload: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    isActive?: boolean;
  }) => api.post('/users', payload),
  update: (id: string, payload: {
    firstName?: string;
    lastName?: string;
    isActive?: boolean;
  }) => api.patch(`/users/${id}`, payload),
  delete: (id: string) => api.delete(`/users/${id}`),
  assignRole: (userId: string, role: string) => 
    api.post('/users/assign-role', { userId, role }),
  removeRole: (userId: string, role: string) => 
    api.post('/users/remove-role', { userId, role }),
  updatePermissions: (userId: string, allowedCompanyIds: string[], allowedJurisdictions: string[]) =>
    api.patch(`/users/${userId}/permissions`, { allowedCompanyIds, allowedJurisdictions }),
};

export const rolesAPI = {
  getAll: () => api.get('/roles'),
};

export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (payload: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => api.post('/auth/register', payload),
  logout: (refreshToken: string) =>
    api.post('/auth/logout', { refreshToken }),
  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (payload: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    bio?: string;
  }) => api.patch('/auth/profile', payload),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  revokeAllTokens: () => api.post('/auth/revoke-all-tokens'),
};

export const auditAPI = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    userId?: string;
    action?: string;
    entityType?: string;
    entityId?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    jurisdiction?: string;
    successOnly?: boolean;
  }) => api.get('/audit', { params }),
  getByUser: (userId: string) => api.get(`/audit/user/${userId}`),
  getByEntity: (type: string, id: string) => api.get(`/audit/entity/${type}/${id}`),
  getStats: (days?: number) => api.get('/audit/stats', { params: { days } }),
  getMyLogs: () => api.get('/audit/me'),
  export: (params?: any) =>
    api.get('/audit/export', {
      params,
      responseType: 'blob',
    }),
};

export const invitationsAPI = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    status?: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
    email?: string;
    invitedBy?: string;
  }) => api.get('/admin/invitations', { params }),
  getById: (id: string) => api.get(`/admin/invitations/${id}`),
  create: (payload: {
    email: string;
    role: string;
    message?: string;
    notes?: string;
  }) => api.post('/admin/invitations', payload),
  resend: (id: string) => api.post(`/admin/invitations/${id}/resend`),
  revoke: (id: string) => api.delete(`/admin/invitations/${id}`),
  validate: (token: string) => api.get(`/admin/invitations/validate/${token}`),
};

export const emailAdminAPI = {
  getSuppressionList: () => api.get('/admin/email/suppression-list'),
  checkSuppression: (email: string) => api.get(`/admin/email/suppression-list/${encodeURIComponent(email)}`),
  removeSuppression: (email: string) => api.delete(`/admin/email/suppression-list/${encodeURIComponent(email)}`),
  getDeliveryLog: (params?: {
    page?: number;
    limit?: number;
    email?: string;
    successOnly?: boolean;
  }) => api.get('/admin/email/delivery-log', { params }),
  sendTestEmail: (email: string) => api.post(`/admin/email/test-send/${encodeURIComponent(email)}`),
  getStatus: () => api.get('/admin/email/status'),
};

export const accountsAPI = {
  getPending: () => api.get('/admin/accounts/pending'),
  getPendingCount: () => api.get('/admin/accounts/pending/count'),
  getStatus: (userId: string) => api.get(`/admin/accounts/${userId}/status`),
  approve: (userId: string, payload: {
    role?: string;
    notes?: string;
  }) => api.post(`/admin/accounts/${userId}/approve`, payload),
  reject: (userId: string, payload: {
    reason: string;
  }) => api.post(`/admin/accounts/${userId}/reject`, payload),
  bulkApprove: (payload: {
    userIds: string[];
    role?: string;
  }) => api.post('/admin/accounts/bulk-approve', payload),
};
